import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { CompanyContactDialogAction } from "./company-contact-dialog.actions";
import { CompanyContactRequestService } from "../../services/request/company-contact-request.service";
import { finalize, Observable, throwError } from "rxjs";
import { map } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { ResetStateDefaults } from "../base-action-class.model";
import { CompanyContactDtoModel } from '../../models/company-contact/company-contact-dto.model';
import { OrganizationUtils } from '../../utils/OrganizationUtils';

export enum CompanyContactDialogMode {
  CREATE = 'CREATE',
  EDIT = 'EDIT'
}

interface CompanyContactDialogStateModel {
  active: boolean;
  mode: CompanyContactDialogMode;
  companyContactId: number;
  companyContact: CompanyContactDtoModel;
  updatedCompanyContact: CompanyContactDtoModel;
  inProgress: boolean;
  disabled: boolean;
}

const getDefaults = (): CompanyContactDialogStateModel => ({
  active: false,
  mode: null,
  companyContactId: null,
  companyContact: null,
  updatedCompanyContact: null,
  inProgress: false,
  disabled: false
});

@State<CompanyContactDialogStateModel>({
  name: 'companyContactDialog',
  defaults: getDefaults()
})
@Injectable()
export class CompanyContactDialogXsState {

  //region CONSTRUCTOR
  constructor(
    private companyContactRequestService: CompanyContactRequestService,
    private store: Store
  ) {}
  //endregion

  //region SELECTOR
  @Selector()
  static active(state: CompanyContactDialogStateModel): boolean {
    return state.active;
  }

  @Selector()
  static mode(state: CompanyContactDialogStateModel): CompanyContactDialogMode {
    return state.mode;
  }

  @Selector()
  static companyContact(state: CompanyContactDialogStateModel): CompanyContactDtoModel {
    return state.companyContact;
  }

  @Selector()
  static inProgress(state: CompanyContactDialogStateModel): boolean {
    return state.inProgress;
  }

  @Selector()
  static disbaled(state: CompanyContactDialogStateModel): boolean {
    return state.disabled;
  }

  @Selector()
  static isSystemUser(state: CompanyContactDialogStateModel): boolean {
    return state.companyContact?.authorOrgId === OrganizationUtils.INSTANCE_ROOT_ORG_ID;
  }
  //endregion

  //region ACTION
  @Action(CompanyContactDialogAction.Create)
  create(ctx: StateContext<CompanyContactDialogStateModel>): void {
    const state: CompanyContactDialogStateModel = ctx.getState();

    if (state.active) {
      throw new Error('State is already active!');
    }

    ctx.patchState({
      active: true,
      companyContactId: null,
      companyContact: null,
      updatedCompanyContact: null,
      mode: CompanyContactDialogMode.CREATE
    });
  }

  @Action(CompanyContactDialogAction.Edit)
  update(
    ctx: StateContext<CompanyContactDialogStateModel>,
    action: CompanyContactDialogAction.Edit
  ): Observable<void> {
    const state: CompanyContactDialogStateModel = ctx.getState();

    if (!action.companyContactId) {
      return throwError(() => new Error('Company contact id cannot be null!'));
    }

    if (state.active) {
      return throwError(() => new Error('State is already active!'));
    }

    ctx.patchState({
      active: true,
      inProgress: true,
      mode: CompanyContactDialogMode.EDIT
    });

    return this.companyContactRequestService.getCompanyContact(action.companyContactId)
      .pipe(
        map((companyContact: CompanyContactDtoModel) => {
          ctx.patchState({
            active: true,
            companyContactId: action.companyContactId,
            companyContact,
            updatedCompanyContact: {...companyContact},
            disabled: companyContact.isEntityPresent
          });
        }),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  @Action(CompanyContactDialogAction.UpdateState)
  updateState(
    ctx: StateContext<CompanyContactDialogStateModel>,
    action: CompanyContactDialogAction.UpdateState
  ): void {
    const state: CompanyContactDialogStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    ctx.patchState({updatedCompanyContact: action.companyContact});
  }

  @Action(CompanyContactDialogAction.Save)
  save(ctx: StateContext<CompanyContactDialogStateModel>): Observable<void> {
    const state: CompanyContactDialogStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    ctx.patchState({inProgress: true});

    let request: Observable<any>;

    if (state.mode === CompanyContactDialogMode.EDIT) {
      request = this.companyContactRequestService.updateCompanyContact(
        state.companyContactId,
        state.updatedCompanyContact
      );
    } else {
      request = this.companyContactRequestService.createCompanyContact(state.updatedCompanyContact);
    }

    ctx.patchState({inProgress: true});

    return request
      .pipe(
        map(() => {
          this.store.dispatch(new CompanyContactDialogAction.Close());
        }),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  @Action(CompanyContactDialogAction.Delete)
  delete(ctx: StateContext<CompanyContactDialogStateModel>): Observable<void> {
    const state: CompanyContactDialogStateModel = ctx.getState();

    if (!state.active) {
      return throwError(() => new Error('State is not active!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    if (state.mode != CompanyContactDialogMode.EDIT) {
      return throwError(() => new Error('Delete is only allowed in EDIT mode!'));
    }

    ctx.patchState({inProgress: true});

    return this.companyContactRequestService.deleteCompanyContact(state.companyContactId)
      .pipe(
        map(() => {
          this.store.dispatch(new CompanyContactDialogAction.Close());
        }),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  @Action([
    CompanyContactDialogAction.Close,
    CompanyContactDialogAction.Reset,
    ResetStateDefaults
  ])
  reset(ctx: StateContext<CompanyContactDialogStateModel>): void {
    ctx.setState(getDefaults);
  }
  //endregion
}
